/**
 * REEDOS Knowledge Base — frontend API client + React hooks.
 *
 * Thin wrappers around the /api/kb/* surface (see backend/routes_kb.py).
 * Every function returns a Promise resolving to the parsed response
 * body or throws the axios error.
 *
 * Hooks (useKbNavigation, useKbArticle, useKbSearch, ...) encapsulate
 * loading/error state and expose a stable API so components stay
 * declarative.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";

// ─────────────────────────────────────────────────────────────────────
// Raw HTTP clients
// ─────────────────────────────────────────────────────────────────────
export async function kbFetchConfig() {
    const { data } = await api.get("/kb/config");
    return data;
}

export async function kbListArticles(params = {}) {
    const { data } = await api.get("/kb/articles", { params });
    return data;
}

export async function kbGetArticle(slug) {
    const encoded = encodeSlug(slug);
    const { data } = await api.get(`/kb/article/${encoded}`);
    return data;
}

export async function kbSearch(q, { kinds = "articles,workflows", limit = 20 } = {}) {
    const { data } = await api.get("/kb/search", { params: { q, kinds, limit } });
    return data;
}

export async function kbGetNavigation(product = undefined) {
    const params = product ? { product } : {};
    const { data } = await api.get("/kb/navigation", { params });
    return data;
}

export async function kbGetContext(route) {
    const { data } = await api.get("/kb/context", { params: { route } });
    return data;
}

export async function kbGetFieldHelp(slug, field) {
    const { data } = await api.get("/kb/field-help", { params: { slug, field } });
    return data;
}

export async function kbListWorkflows(params = {}) {
    const { data } = await api.get("/kb/workflows", { params });
    return data;
}

export async function kbGetWorkflow(slug) {
    const encoded = encodeSlug(slug);
    const { data } = await api.get(`/kb/workflow/${encoded}`);
    return data;
}

export async function kbListVersions(limit = 50) {
    const { data } = await api.get("/kb/versions", { params: { limit } });
    return data;
}

// Analytics — best-effort; failures are silent (never block the UI).
export async function kbRecordView({ article_slug, source = "sidebar", dwell_ms }) {
    try {
        await api.post("/kb/analytics/view", { article_slug, source, dwell_ms });
    } catch (_err) {
        // Analytics is fire-and-forget; PII sanitisation is server-side.
    }
}

export async function kbRecordSearch({ query, results_count, clicked_slug = null }) {
    try {
        await api.post("/kb/analytics/search", { query, results_count, clicked_slug });
    } catch (_err) {
        // ignored
    }
}

// Slug may contain forward slashes; encode each segment but keep them.
function encodeSlug(slug) {
    return String(slug).split("/").map(encodeURIComponent).join("/");
}

// ─────────────────────────────────────────────────────────────────────
// React hooks
// ─────────────────────────────────────────────────────────────────────
/**
 * Generic single-shot loader: pass a promise-returning fn and (optional)
 * deps. Returns `{ data, loading, error, reload }`.
 */
function useKbLoader(fn, deps = []) {
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);
    const mountedRef = useRef(true);

    const reload = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const d = await fn();
            if (mountedRef.current) setData(d);
        } catch (err) {
            if (mountedRef.current) setError(err);
        } finally {
            if (mountedRef.current) setLoading(false);
        }
    // The consumer supplies the dependency list; we can't statically
    // introspect it.  Intentional — see hook contract.
    }, deps);

    useEffect(() => {
        mountedRef.current = true;
        reload();
        return () => {
            mountedRef.current = false;
        };
    }, [reload]);

    return { data, loading, error, reload };
}

export function useKbConfig() {
    return useKbLoader(() => kbFetchConfig(), []);
}

export function useKbNavigation(product) {
    return useKbLoader(() => kbGetNavigation(product), [product]);
}

export function useKbArticle(slug) {
    return useKbLoader(() => kbGetArticle(slug), [slug]);
}

export function useKbWorkflow(slug) {
    return useKbLoader(() => kbGetWorkflow(slug), [slug]);
}

export function useKbContext(route) {
    // route may be null/undefined; skip the call entirely then.
    return useKbLoader(
        () => (route ? kbGetContext(route) : Promise.resolve({ primary: null, related: [] })),
        [route],
    );
}

/**
 * Debounced search hook.
 *
 * Fires an analytics event with the sanitised query + results_count
 * every time the user's typed query stabilises for >300ms.
 */
export function useKbSearch(query, { limit = 20, minChars = 2, debounceMs = 300 } = {}) {
    const [state, setState] = useState({ data: null, loading: false, error: null });
    useEffect(() => {
        if (!query || query.trim().length < minChars) {
            setState({ data: null, loading: false, error: null });
            return undefined;
        }
        setState((s) => ({ ...s, loading: true, error: null }));
        const handle = setTimeout(async () => {
            try {
                const d = await kbSearch(query, { limit });
                setState({ data: d, loading: false, error: null });
                kbRecordSearch({ query, results_count: d.results.length });
            } catch (err) {
                setState({ data: null, loading: false, error: err });
            }
        }, debounceMs);
        return () => clearTimeout(handle);
    }, [query, limit, minChars, debounceMs]);
    return state;
}
